import { Link } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, Check, User, Car, Calendar, FileCheck2, Bell,
  CalendarClock, RotateCcw, AlertTriangle, ClipboardList, Wrench, Users,
  Star, Sparkles, HelpCircle,
} from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import SEO from "@/components/SEO";
import BusinessProductWalkthrough from "@/components/BusinessProductWalkthrough";

/**
 * Business-facing homepage: rebuilt 2026-08-27 around Philippine MSME
 * research rather than generic SaaS positioning. Central angle: don't sell
 * ConfirmPH as replacing the owner's way of doing business: sell it as
 * the missing layer behind it. "Messenger gets the conversation. ConfirmPH
 * keeps the rental clear." See the homepage brief (biz.* keys in
 * i18n/translations.js) for the full section-by-section reasoning.
 *
 * The interactive demo (BusinessProductWalkthrough) is kept exactly where
 * it was, unmodified in its own internals: only its header copy and CTA
 * labels were updated to match this page's new language.
 */
export default function Businesses() {
  const { t } = useT();
  return (
    <div data-testid="businesses-landing">
      <SEO
        title="ConfirmPH for Rental Businesses: Booking Confirmation & Management Tools"
        description="ConfirmPH gives Philippine rental businesses booking confirmation links, reminders, vehicle records, inspections and clearer customer communication."
        canonical="/businesses"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-12 sm:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> {t("biz.hero.badge")}
            </div>
            <h1 className="mt-5 font-display font-bold tracking-tight text-slate-900 leading-[1.08] text-4xl sm:text-5xl lg:text-[3.25rem]">
              {t("biz.hero.title.a")}
              <br /><span className="text-[#1B4332]">{t("biz.hero.title.b")}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              {t("biz.hero.body")}
            </p>
            <p className="mt-3 text-base sm:text-lg font-semibold text-slate-900 leading-relaxed max-w-2xl">
              {t("biz.hero.lead")}
            </p>
            <p className="mt-3 text-sm text-slate-500 max-w-xl" data-testid="biz-hero-recognition">
              {t("biz.hero.recognition")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register?as=business" data-testid="biz-register-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-semibold">
                {t("biz.cta.try5")} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#demo" data-testid="biz-how-it-works-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium">
                {t("biz.hero.cta.secondary")}
              </a>
            </div>
            <p className="mt-3 text-xs text-slate-500" data-testid="biz-trust-note">{t("biz.hero.microcopy")}</p>

            <div className="mt-4 text-xs text-slate-500">
              {t("biz.hero.demo")} →{" "}
              <Link to="/login?as=business" data-testid="biz-demo-login-link" className="font-medium text-[#1B4332] underline underline-offset-4">
                demo@confirmph.app
              </Link>{" "}
              / <span className="font-mono">demo1234</span>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up">
            <BizHeroVisual t={t} />
          </div>
        </div>
      </section>

      {/* Parang familiar?: self-recognition */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.recognition.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.recognition.title")}</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="biz-recognition-cards">
            {[t("biz.recognition.q1"), t("biz.recognition.q2"), t("biz.recognition.q3"), t("biz.recognition.q4"), t("biz.recognition.q5"), t("biz.recognition.q6")].map((q, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-5 text-slate-800 font-medium">
                {q}
              </div>
            ))}
          </div>
          <p className="mt-10 text-center font-display text-xl sm:text-2xl font-semibold max-w-2xl mx-auto">
            {t("biz.recognition.payoff")}
          </p>
        </div>
      </section>

      {/* Keep what already works */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.keep.eyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
            {t("biz.keep.title.a")}<br />{t("biz.keep.title.b")}
          </h2>

          <div className="mt-10 space-y-3">
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-3.5 font-medium text-slate-800">
              {t("biz.keep.channels")}
            </div>
            <div className="text-slate-300 text-xl" aria-hidden="true">↓</div>
            <div className="text-sm text-slate-600">{t("biz.keep.talk")}</div>
            <div className="text-slate-300 text-xl" aria-hidden="true">↓</div>
            <div className="rounded-xl bg-[#1B4332] text-white px-5 py-4">
              <div className="font-display font-bold">ConfirmPH</div>
              <div className="text-sm text-white/80 mt-0.5">{t("biz.keep.record")}</div>
            </div>
          </div>

          <p className="mt-8 text-sm text-slate-600">{t("biz.keep.note")}</p>
          <p className="mt-6 font-display text-lg sm:text-xl font-semibold">
            {t("biz.keep.payoff.a")}<br />{t("biz.keep.payoff.b")}
          </p>
        </div>
      </section>

      {/* When "small mistake lang" isn't small anymore */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.consequence.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.consequence.title")}</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="biz-consequence-cards">
            {[
              [t("biz.consequence.1.t"), t("biz.consequence.1.d")],
              [t("biz.consequence.2.t"), t("biz.consequence.2.d")],
              [t("biz.consequence.3.t"), t("biz.consequence.3.d")],
              [t("biz.consequence.4.t"), t("biz.consequence.4.d")],
            ].map(([title, desc], i) => (
              <div key={i} className="rounded-xl border border-rose-100 bg-rose-50/50 p-5">
                <div className="flex items-start gap-2 text-rose-800">
                  <AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />
                  <div className="font-display font-semibold">{title}</div>
                </div>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center font-display text-xl sm:text-2xl font-semibold max-w-2xl mx-auto">
            {t("biz.consequence.payoff.a")}<br />{t("biz.consequence.payoff.b")}
          </p>
        </div>
      </section>

      {/* One rental. One record. */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.standard.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.standard.title")}</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="biz-standard-cards">
            {[
              [User, t("biz.standard.q1")],
              [Car, t("biz.standard.q2")],
              [Calendar, t("biz.standard.q3")],
              [FileCheck2, t("biz.standard.q4")],
              [Bell, t("biz.standard.q5")],
            ].map(([Icon, label], i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 text-center">
                <div className="w-9 h-9 mx-auto rounded-lg bg-emerald-50 text-[#1B4332] grid place-items-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-3 font-display font-semibold">{label}</div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-slate-600 max-w-xl mx-auto">{t("biz.standard.note")}</p>
          <p className="mt-4 text-center font-display text-xl sm:text-2xl font-bold text-[#1B4332]">{t("biz.standard.rule")}</p>
          <p className="mt-4 text-center text-sm text-slate-500">
            {t("biz.standard.rule.before")} <ArrowRight className="w-3.5 h-3.5 inline" /> {t("biz.standard.rule.after")}
          </p>
        </div>
      </section>

      {/* Start the day clear */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.today.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.today.title")}</h2>
          </div>
          <div className="mt-10 max-w-3xl mx-auto rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="biz-today-panel">
            <div className="flex items-center gap-1.5 px-4 h-9 border-b border-slate-200 bg-slate-50">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
              <span className="ml-3 text-[11px] font-mono text-slate-400">confirmph.app/app</span>
            </div>
            <div className="p-5 sm:p-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                [CalendarClock, t("biz.today.pickups"), "4", "emerald"],
                [RotateCcw, t("biz.today.returns"), "3", "emerald"],
                [FileCheck2, t("biz.today.waiting"), "2", "amber"],
                [Wrench, t("biz.today.issues"), "1", "rose"],
                [Bell, t("biz.today.followups"), "3", "amber"],
              ].map(([Icon, label, value, tone], i) => (
                <div key={i} className={`rounded-lg border p-3 ${tone === "rose" ? "border-rose-200 bg-rose-50" : tone === "amber" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
                  <Icon className={`w-4 h-4 ${tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-emerald-700"}`} />
                  <div className="mt-2 text-2xl font-display font-bold">{value}</div>
                  <div className="text-[11px] text-slate-600 leading-tight mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-8 text-center font-display text-lg sm:text-xl font-semibold">{t("biz.today.cta")}</p>
          <p className="mt-3 text-center text-sm text-slate-600 max-w-xl mx-auto">{t("biz.today.benefit")}</p>
          <p className="mt-2 text-center text-sm font-medium text-[#1B4332]">{t("biz.today.owner")}</p>
        </div>
      </section>

      {/* Real product. Demo data.: the interactive demo, untouched internals */}
      <BusinessProductWalkthrough />

      {/* "Maliit pa kami." */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.small.eyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.small.title")}</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{t("biz.small.body")}</p>
          <p className="mt-2 text-slate-600 leading-relaxed">{t("biz.small.body2")}</p>
          <p className="mt-6 font-display text-xl sm:text-2xl font-semibold">
            {t("biz.small.note.a")}<br />{t("biz.small.note.b")}
          </p>
          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
            {[t("biz.small.reassure1"), t("biz.small.reassure2"), t("biz.small.reassure3")].map((r, i) => (
              <li key={i} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-700" /> {r}</li>
            ))}
          </ul>
          <div className="mt-7">
            <Link to="/register?as=business" data-testid="biz-small-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-semibold">
              {t("biz.cta.try5")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Hindi kailangan maging techy */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.simple.eyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.simple.title")}</h2>
          <div className="mt-8 inline-flex flex-wrap items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-700">
            {t("biz.simple.flow")}
          </div>
          <div className="mt-8 max-w-md mx-auto rounded-xl border border-emerald-200 bg-emerald-50/50 p-5 text-left flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-semibold">{t("biz.simple.help.t")}</div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{t("biz.simple.help.d")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Customers don't see your system. They feel it. */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.feel.eyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.feel.title")}</h2>
          <ul className="mt-8 space-y-2.5 max-w-lg mx-auto text-left" data-testid="biz-feel-list">
            {[t("biz.feel.1"), t("biz.feel.2"), t("biz.feel.3"), t("biz.feel.4"), t("biz.feel.5")].map((line, i) => (
              <li key={i} className="flex items-start gap-2.5 text-slate-700">
                <Sparkles className="w-4 h-4 mt-0.5 text-[#1B4332] flex-shrink-0" />
                {line}
              </li>
            ))}
          </ul>
          <p className="mt-8 font-display text-xl sm:text-2xl font-semibold">{t("biz.feel.payoff")}</p>
          <p className="mt-2 text-sm text-slate-600">{t("biz.feel.note")}</p>
        </div>
      </section>

      {/* Less sisihan. More clarity. */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <h2 className="text-center font-display text-3xl sm:text-4xl font-bold">{t("biz.clarity.eyebrow")}</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Less</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-500 italic">
                <li>{t("biz.clarity.less1")}</li>
                <li>{t("biz.clarity.less2")}</li>
                <li>{t("biz.clarity.less3")}</li>
              </ul>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">More</div>
              <p className="mt-3 font-display font-semibold text-emerald-900">{t("biz.clarity.more")}</p>
              <ul className="mt-3 space-y-1.5 text-sm text-emerald-900">
                {[t("biz.clarity.1"), t("biz.clarity.2"), t("biz.clarity.3"), t("biz.clarity.4"), t("biz.clarity.5")].map((l, i) => (
                  <li key={i} className="flex items-center gap-2"><Check className="w-3.5 h-3.5" /> {l}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 text-center text-sm text-slate-600 max-w-xl mx-auto">{t("biz.clarity.note")}</p>
        </div>
      </section>

      {/* Before you compare the price */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.money.eyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.money.title")}</h2>
          <ul className="mt-8 space-y-2 text-left max-w-md mx-auto text-sm text-slate-700" data-testid="biz-money-list">
            {[t("biz.money.1"), t("biz.money.2"), t("biz.money.3"), t("biz.money.4"), t("biz.money.5")].map((l, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 flex-shrink-0" /> {l}
              </li>
            ))}
          </ul>
          <p className="mt-8 font-display text-lg sm:text-xl font-semibold">
            {t("biz.money.note.a")}<br />{t("biz.money.note.b")}
          </p>
        </div>
      </section>

      {/* Protect the bookings you worked hard to get */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.protect.eyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("biz.protect.title")}</h2>
          <ul className="mt-8 space-y-1.5 text-slate-700 text-sm sm:text-base">
            <li>{t("biz.protect.1")}</li>
            <li>{t("biz.protect.2")}</li>
            <li>{t("biz.protect.3")}</li>
            <li>{t("biz.protect.4")}</li>
            <li>{t("biz.protect.5")}</li>
          </ul>
          <p className="mt-8 font-display text-xl sm:text-2xl font-semibold max-w-xl mx-auto">{t("biz.protect.note")}</p>
        </div>
      </section>

      {/* RUN / TRUST / GROW: compressed from the old two giant feature grids */}
      <section id="groups" className="bg-white border-t border-slate-200 scroll-mt-24" data-testid="biz-groups">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("biz.groups.eyebrow")}</div>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B4332] text-white grid place-items-center"><ClipboardList className="w-5 h-5" strokeWidth={1.8} /></div>
              <h3 className="mt-4 font-display text-lg font-semibold">{t("biz.groups.run.t")}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("biz.groups.run.d")}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B4332] text-white grid place-items-center"><Star className="w-5 h-5" strokeWidth={1.8} /></div>
              <h3 className="mt-4 font-display text-lg font-semibold">{t("biz.groups.trust.t")}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("biz.groups.trust.d")}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B4332] text-white grid place-items-center"><Users className="w-5 h-5" strokeWidth={1.8} /></div>
              <h3 className="mt-4 font-display text-lg font-semibold">{t("biz.groups.grow.t")}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("biz.groups.grow.d")}</p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/businesses/pricing" data-testid="biz-groups-cta" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B4332] hover:underline">
              {t("biz.groups.cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#10241B] text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
            {t("biz.final.title.a")}<br />{t("biz.final.title.b")}
          </h2>
          <p className="mt-5 text-white/70 text-sm sm:text-base space-y-0.5">
            <span className="block">{t("biz.final.q1")}</span>
            <span className="block">{t("biz.final.q2")}</span>
            <span className="block">{t("biz.final.q3")}</span>
            <span className="block">{t("biz.final.q4")}</span>
            <span className="block text-white">{t("biz.final.q5")}</span>
          </p>
          <p className="mt-6 font-display text-xl sm:text-2xl font-semibold">{t("biz.final.instruction")}</p>
          <p className="mt-3 text-white/70 text-sm">
            {t("biz.final.keep1")} {t("biz.final.keep2")} {t("biz.final.keep3")} {t("biz.final.keep4")}
          </p>
          <p className="mt-1 text-white font-medium text-sm sm:text-base">{t("biz.final.instruction2")}</p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register?as=business" data-testid="biz-final-register" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-[#10241B] font-semibold hover:bg-white/90">
              {t("biz.final.cta.primary")} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#demo" data-testid="biz-final-demo" className="inline-flex items-center px-6 py-3 rounded-lg border border-white/30 text-white font-semibold hover:bg-white/10">
              {t("biz.final.cta.secondary")}
            </a>
          </div>
          <p className="mt-4 text-xs text-white/60">{t("biz.final.microcopy")}</p>

          <div className="mt-8 pt-8 border-t border-white/15">
            <p className="font-display text-lg font-semibold">{t("biz.final.brandline")}</p>
          </div>

          <div className="mt-6">
            <Link to="/business-terms" data-testid="biz-terms-link" className="text-xs text-white/60 underline hover:text-white">
              Read business terms
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Hero visual: replaces the previous stock "tropical coastal road" photo.
 * A staff chat with a contradiction, next to the single ConfirmPH record
 * that resolves it. Same self-persuasion technique as the renter homepage's
 * chat-vs-record visual: no feature claim, just recognition.
 */
function BizHeroVisual({ t }) {
  return (
    <div>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="biz-hero-visual">
        <div className="p-5 sm:p-6 bg-[#EFEAE2]">
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-white px-3.5 py-2 text-sm shadow-sm">
              {t("biz.hero.visual.chat.customer")}
            </div>
          </div>
          <div className="mt-2 flex justify-start">
            <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-emerald-600 text-white px-3.5 py-2 text-sm shadow-sm">
              {t("biz.hero.visual.chat.staff1")}
            </div>
          </div>
          <div className="mt-2 flex justify-start">
            <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-amber-500 text-white px-3.5 py-2 text-sm shadow-sm">
              {t("biz.hero.visual.chat.staff2")}
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-3 text-center text-xs sm:text-sm font-medium text-slate-500 bg-white border-y border-dashed border-slate-200">
          {t("biz.hero.visual.chat.question")}
        </div>

        <div className="p-5 sm:p-6 bg-white">
          <div className="flex items-center gap-2 text-emerald-800">
            <div className="w-6 h-6 rounded-full bg-emerald-100 grid place-items-center flex-shrink-0">
              <Check className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.14em]">{t("biz.hero.visual.record.badge")}</span>
          </div>

          <div className="mt-3 font-display text-lg font-semibold text-slate-900">{t("biz.hero.visual.record.vehicle")}</div>
          <div className="text-sm text-slate-600">{t("biz.hero.visual.record.dates")}</div>

          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            <RecordField label={t("biz.hero.visual.record.customer.label")} value={t("biz.hero.visual.record.customer.value")} />
            <RecordField label={t("biz.hero.visual.record.pickup.label")} value={t("biz.hero.visual.record.pickup.value")} />
            <RecordField label={t("biz.hero.visual.record.price.label")} value={t("biz.hero.visual.record.price.value")} />
            <RecordField label={t("biz.hero.visual.record.deposit.label")} value={t("biz.hero.visual.record.deposit.value")} />
            <RecordField label={t("biz.hero.visual.record.status.label")} value={t("biz.hero.visual.record.status.value")} />
          </dl>
        </div>
      </div>
      <p className="mt-4 text-center text-sm sm:text-base font-medium text-slate-700">
        {t("biz.hero.visual.payoff")}
      </p>
    </div>
  );
}

function RecordField({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-900">{value}</dd>
    </div>
  );
}
